export interface AIPhoneCall {
    id: string;
    practice_id: string;
    patient_phone: string;
    patient_id?: string;
    patient_name?: string;
    call_started_at: string;
    call_ended_at?: string;
    duration_seconds?: number;
    intent_detected?: string;
    status?: AIPhoneStatus;
    outcome?: AIPhoneOutcome;
    transcript?: string;
    summary?: string;
    transcript_summary?: string;
    sentiment?: "positive" | "neutral" | "negative";
    quality_score?: number;
    appointment_id?: string;
    guest_appointment_id?: string;
    call_type?: string;
    call_direction?: "inbound" | "outbound";
    routed_to_inbox?: AIPhoneInboxRoute;
    is_emergency?: boolean;
    appointment_type?: string;
    preferred_date?: string;
    blocked_content?: boolean;
    block_reason?: string;
    handled_by?: string;
    handled_at?: string;
    analysis_data?: Record<string, unknown>;
    created_at: string;

    // Screen-friendly aliases (mapped in service layer)
    callerName?: string;
    callerPhone?: string;
    startedAt?: string;
    duration?: number;
    direction?: "inbound" | "outbound";
    reason?: string;
}

export type AIPhoneOutcome =
    | "booking_created"
    | "booking_rescheduled"
    | "booking_cancelled"
    | "callback_requested"
    | "prescription_requested"
    | "info_provided"
    | "waitlist_added"
    | "transferred_to_human"
    | "no_action"
    | "voicemail"
    | "appointment_booked"
    | "resolved_by_practice"
    | "forwarded_to_doctor"
    | "error";

export type AIPhoneStatus =
    | "in_progress"
    | "completed"
    | "failed"
    | "pending"
    | "escalated"
    | "missed"
    | "transferred"
    | "archived"
    | "transcribed"
    | "routed"
    | "handled";

export type AIPhoneInboxRoute = "reception" | "mfa" | "doctor" | "billing";

export interface AIPhoneCallFilters {
    practiceId?: string;
    dateFrom?: string;
    dateTo?: string;
    outcome?: AIPhoneOutcome;
    status?: "in_progress" | "completed" | "failed" | "pending" | "escalated" | "missed" | "transferred" | "all";
    routedTo?: AIPhoneInboxRoute | "all";
    patientId?: string;
    hasAppointment?: boolean;
    limit?: number;
    period?: "today" | "week" | "month" | "all";
    search?: string;
}

export interface PhoneStats {
    todayCalls: number;
    missedCalls: number;
    aiHandled: number;
    avgQuality: number;
    appointmentsBooked: number;
    callbacksRequested: number;
    reviewRequired: number;
    // Convenience aliases used by dashboard/screens
    today: number;
    thisWeek: number;
    bookingRate: number;
    avgDuration: number;
    peakHour: string;
    aiHandledRate: number;
}

export interface RecentCall {
    id: string;
    callerName: string;
    callerPhone: string;
    time: string;
    duration: string;
    status: "completed" | "missed" | "transferred" | "escalated" | "in_progress";
    summary?: string;
    outcome?: AIPhoneOutcome;
    sentiment?: "positive" | "neutral" | "negative";
}

export type PhonePeriodFilter = "today" | "week" | "month";

const AI_PHONE_FILTER_OUTCOME_EQUIVALENTS: Record<AIPhoneOutcome, readonly AIPhoneOutcome[]> = {
    booking_created: ["booking_created", "appointment_booked"],
    booking_rescheduled: ["booking_rescheduled"],
    booking_cancelled: ["booking_cancelled"],
    callback_requested: ["callback_requested"],
    prescription_requested: ["prescription_requested"],
    info_provided: ["info_provided"],
    waitlist_added: ["waitlist_added"],
    transferred_to_human: ["transferred_to_human"],
    no_action: ["no_action"],
    voicemail: ["voicemail"],
    appointment_booked: ["appointment_booked", "booking_created"],
    resolved_by_practice: ["resolved_by_practice"],
    forwarded_to_doctor: ["forwarded_to_doctor"],
    error: ["error"],
};

type AIPhoneStatusFilterValue = Exclude<NonNullable<AIPhoneCallFilters["status"]>, "all">;

const AI_PHONE_FILTER_STATUS_EQUIVALENTS: Record<AIPhoneStatusFilterValue, readonly AIPhoneStatus[]> = {
    in_progress: ["in_progress", "transcribed", "routed"],
    completed: ["completed", "handled"],
    failed: ["failed"],
    pending: ["pending"],
    escalated: ["escalated"],
    missed: ["missed"],
    transferred: ["transferred"],
};

const AI_PHONE_STATUS_NORMALIZATION_MAP: Partial<Record<AIPhoneStatus, AIPhoneStatus>> = {
    transcribed: "in_progress",
    routed: "in_progress",
    handled: "completed",
};

const AI_PHONE_LEGACY_OUTCOME_MAP: Partial<Record<string, AIPhoneOutcome>> = {
    "Termin gebucht": "booking_created",
    "Termin vereinbart": "appointment_booked",
    "Rückruf gewünscht": "callback_requested",
    "An Arzt weitergeleitet": "forwarded_to_doctor",
};

export const AI_PHONE_INBOX_ROUTE_LABELS: Record<AIPhoneInboxRoute | "all", string> = {
    all: "Alle Anrufe",
    reception: "Rezeption",
    mfa: "MFA",
    doctor: "Arzt",
    billing: "Abrechnung",
};

export function getAIPhoneOutcomeFilterValues(outcome: AIPhoneOutcome): readonly AIPhoneOutcome[] {
    return AI_PHONE_FILTER_OUTCOME_EQUIVALENTS[outcome] ?? [outcome];
}

export function getAIPhoneStatusFilterValues(
    status: AIPhoneStatusFilterValue,
): readonly AIPhoneStatus[] {
    return AI_PHONE_FILTER_STATUS_EQUIVALENTS[status] ?? [status];
}

export function normalizeAIPhoneOutcome(outcome?: string | null): AIPhoneOutcome | undefined {
    if (!outcome) return undefined;
    return (AI_PHONE_LEGACY_OUTCOME_MAP[outcome] ?? outcome) as AIPhoneOutcome;
}

export function normalizeAIPhoneStatus(status?: string | null): AIPhoneStatus | undefined {
    if (!status) return undefined;
    const typedStatus = status as AIPhoneStatus;
    return AI_PHONE_STATUS_NORMALIZATION_MAP[typedStatus] ?? typedStatus;
}

export function normalizeAIPhoneInboxRoute(
    routedTo?: string | null,
    outcome?: string | null,
): AIPhoneInboxRoute | undefined {
    if (routedTo === "reception" || routedTo === "mfa" || routedTo === "doctor" || routedTo === "billing") {
        return routedTo;
    }

    if (normalizeAIPhoneOutcome(outcome) === "forwarded_to_doctor") {
        return "doctor";
    }

    return undefined;
}

export const OUTCOME_LABELS: Record<AIPhoneOutcome, string> = {
    booking_created: "Termin gebucht",
    booking_rescheduled: "Termin verschoben",
    booking_cancelled: "Termin storniert",
    callback_requested: "Rückruf erbeten",
    prescription_requested: "Rezeptanfrage",
    info_provided: "Info erteilt",
    waitlist_added: "Warteliste",
    transferred_to_human: "Weitergeleitet",
    no_action: "Keine Aktion",
    voicemail: "Mailbox",
    appointment_booked: "Termin vereinbart",
    resolved_by_practice: "Praxis erledigt",
    forwarded_to_doctor: "An Arzt weitergeleitet",
    error: "Fehler",
};

export const OUTCOME_COLORS: Record<AIPhoneOutcome, string> = {
    booking_created: "success",
    booking_rescheduled: "brand",
    booking_cancelled: "error",
    callback_requested: "warning",
    prescription_requested: "brand",
    info_provided: "neutral",
    waitlist_added: "brand",
    transferred_to_human: "brand",
    no_action: "neutral",
    voicemail: "warning",
    appointment_booked: "success",
    resolved_by_practice: "success",
    forwarded_to_doctor: "brand",
    error: "error",
};
